import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import { Crown, Lock, ArrowRight } from 'lucide-react';

interface FeatureAccessPlaceholderProps {
  featureName: string;
  featureNameAr: string;
  description?: string;
  descriptionAr?: string;
  icon?: React.ReactNode;
}

const FeatureAccessPlaceholder: React.FC<FeatureAccessPlaceholderProps> = ({
  featureName,
  featureNameAr,
  description,
  descriptionAr,
  icon,
}) => {
  const navigate = useNavigate();
  const { language, isRTL } = useLocalization();

  const handleUpgrade = () => {
    navigate('/dashboard/subscriptions', {
      state: {
        upgradeMessage: language === 'ar' 
          ? `${featureNameAr} - متاح في الخطة المهنية`
          : `${featureName} - Available in Professional Plan`
      }
    });
  };

  const defaultDescription = language === 'ar' 
    ? 'هذه الميزة متاحة فقط للمشتركين في الخطة المهنية. قم بالترقية للوصول إلى هذه الميزة والمزيد.'
    : 'This feature is only available for Professional Plan subscribers. Upgrade to access this feature and more.';

  const defaultDescriptionAr = 'هذه الميزة متاحة فقط للمشتركين في الخطة المهنية. قم بالترقية للوصول إلى هذه الميزة والمزيد.';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-primary/10 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 md:p-12 text-center border border-slate-200 dark:border-slate-700">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-primary to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
              {icon || <Lock className="w-12 h-12 text-white" />}
            </div>
          </div>

          {/* Feature Name */}
          <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-4">
            {language === 'ar' ? featureNameAr : featureName}
          </h2>

          {/* Description */}
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 max-w-xl mx-auto">
            {language === 'ar' 
              ? (descriptionAr || defaultDescriptionAr)
              : (description || defaultDescription)
            }
          </p>

          {/* Current Plan Info */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-8">
            <p className="text-yellow-800 dark:text-yellow-200 font-medium">
              {language === 'ar' 
                ? 'الخطة الحالية: مجانية - ترقية للوصول إلى هذه الميزة'
                : 'Current Plan: Free - Upgrade to access this feature'
              }
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleUpgrade}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary to-indigo-600 hover:from-primary-dark hover:to-indigo-700 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 mb-4"
          >
            <Crown className="w-5 h-5" />
            <span>{language === 'ar' ? 'ترقية إلى الخطة المهنية' : 'Upgrade to Professional Plan'}</span>
            <ArrowRight className="w-5 h-5" />
          </button>

          {/* Additional Info */}
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {language === 'ar' 
              ? 'ابدأ تجربة مجانية لمدة 14 يوم'
              : 'Start with a 14-day free trial'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeatureAccessPlaceholder;
